import { createFileRoute, Link } from '@tanstack/react-router';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { FiPlus, FiFilm, FiImage, FiPlay } from 'react-icons/fi';
import { useGetPublicMedia, type MediaDto, isVideoUrl } from '../api/media';
import { useAuth } from '../hooks/useAuth';
import { useState, useEffect } from 'react';
import { MEDIA_FALLBACK_IMAGE_SRC, resolveVideoPosterUrl } from '../utils/mediaFallback';
import { LoadingLogo } from '../components/common/LoadingLogo';

export const Route = createFileRoute('/media/')({
  component: MediaListPage,
});

function MediaListPage() {
  const { isAuthenticated } = useAuth();

  // Paginated public media state
  const [publicPage, setPublicPage] = useState(1);
  const [allPublicMedia, setAllPublicMedia] = useState<MediaDto[]>([]);

  const publicMediaResult = useGetPublicMedia(publicPage);

  const isLoading = publicMediaResult.isLoading;
  const isFetching = publicMediaResult.isFetching;
  const hasMore = !!publicMediaResult.data?.next;

  // Accumulate public media pages
  useEffect(() => {
    if (!publicMediaResult.data?.results?.length) return;
    if (publicPage === 1) {
      setAllPublicMedia(publicMediaResult.data.results as unknown as MediaDto[]);
    } else {
      setAllPublicMedia((prev) => [
        ...prev,
        ...(publicMediaResult.data!.results as unknown as MediaDto[]),
      ]);
    }
  }, [publicMediaResult.data, publicPage]);

  // Reset on auth change
  useEffect(() => {
    setPublicPage(1);
    setAllPublicMedia([]);
  }, [isAuthenticated]);

  const items: MediaDto[] =
    allPublicMedia.length > 0
      ? allPublicMedia
      : ((publicMediaResult.data?.results ?? []) as unknown as MediaDto[]);

  // Show skeleton whenever fetching and nothing to show yet
  const showSkeleton = (isLoading || isFetching) && items.length === 0;

  if (showSkeleton) {
    return <LoadingLogo />;
  }

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* ── Page header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-white text-2xl sm:text-[28px] font-bold">Media</h1>
            <p className="text-zinc-500 text-sm mt-1">
              Explore photos and videos from creators in the community
            </p>
          </div>
          {isAuthenticated && (
            <Link
              to="/media/create"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-[14px] font-semibold px-5 py-2.5 rounded-full transition-all active:scale-95 self-start sm:self-auto"
            >
              <FiPlus size={16} strokeWidth={2.5} />
              Add Media
            </Link>
          )}
        </div>

        {items.length === 0 ? (
          /* ── Empty state ── */
          <div className="flex flex-col items-center justify-center py-24 text-center gap-6">
            <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center text-zinc-600">
              <FiFilm size={34} />
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-white text-xl font-semibold">No media yet</p>
              <p className="text-zinc-500 max-w-xs leading-relaxed text-sm">
                {isAuthenticated
                  ? 'Upload your first photo or video to start building your portfolio.'
                  : 'No public media available yet.'}
              </p>
            </div>
            {isAuthenticated && (
              <Link
                to="/media/create"
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-[14px] font-semibold px-6 py-3 rounded-full transition-all active:scale-95"
              >
                <FiPlus size={16} />
                Add Media
              </Link>
            )}
          </div>
        ) : (
          /* ── Media grid ── */
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {items.map((item) => (
                <MediaCard key={item.id} media={item} />
              ))}
            </div>
            {/* Load more skeleton while fetching */}
            {isFetching && items.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-2xl bg-white/5 border border-white/5 animate-pulse"
                  />
                ))}
              </div>
            )}
            {/* Load more button */}
            {hasMore && !isFetching && (
              <div className="flex justify-center mt-8">
                <button
                  className="px-10 py-3 rounded-full border border-white/20 text-white text-sm font-medium hover:bg-white/5 transition-all active:scale-95"
                  onClick={() => setPublicPage((prev) => prev + 1)}
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
}

// ── Media card ────────────────────────────────────────────────────────────────

function MediaCard({ media }: { media: MediaDto }) {
  const mediaUrls = media.media_urls ?? [];
  const firstUrl = mediaUrls[0];
  const isVideo = media.media_type === 'video' || (firstUrl ? isVideoUrl(firstUrl) : false);
  const count = mediaUrls.length;
  const previewText =
    media.title?.trim() || media.caption?.trim() || media.description?.trim() || 'Media';
  const thumbUrl = firstUrl
    ? isVideo
      ? resolveVideoPosterUrl(mediaUrls, media.video_poster_urls, 0)
      : firstUrl
    : undefined;

  return (
    <Link
      to="/media/$mediaId"
      params={{ mediaId: String(media.id) }}
      className="group relative block aspect-square rounded-2xl overflow-hidden bg-zinc-900 cursor-pointer"
    >
      {/* Thumbnail */}
      {thumbUrl ? (
        <img
          src={thumbUrl}
          alt={previewText}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(event) => {
            const img = event.currentTarget;
            if (img.dataset.fallbackApplied === 'true') return;
            img.dataset.fallbackApplied = 'true';
            img.src = MEDIA_FALLBACK_IMAGE_SRC;
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-zinc-700">
          <FiImage size={32} />
        </div>
      )}

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity" />

      {/* Video badge */}
      {isVideo && (
        <div className="absolute top-2 left-2 p-1.5 rounded-full bg-black/60 text-white pointer-events-none">
          <FiPlay size={11} />
        </div>
      )}

      {/* Multi-item count badge */}
      {count > 1 && (
        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[11px] font-semibold pointer-events-none">
          {count}
        </div>
      )}

      {/* Title preview — always visible, with safe truncation for long text */}
      <div className="absolute inset-x-0 bottom-0 p-3 pt-8 bg-linear-to-t from-black/90 via-black/60 to-transparent pointer-events-none">
        <p className="text-white/95 text-[13px] line-clamp-2 leading-snug font-bold tracking-tight">
          {previewText}
        </p>
      </div>
    </Link>
  );
}
